class StoreHeader extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.occupiedHeight = undefined;
    this.flowHeight = undefined;
    const options = { signal: this.controller.signal };
    this.navigation = this.querySelector('[data-header-navigation]');
    this.menuToggle = this.querySelector('[data-menu-toggle]');
    this.menuShell = this.querySelector('[data-menu-shell]');
    this.drawer = this.querySelector('[data-header-drawer]');
    this.drawerTitle = this.querySelector('[data-menu-title]');
    this.back = this.querySelector('[data-menu-back]');
    this.branches = [...this.querySelectorAll('[data-menu-branch]')];
    this.categories = [...this.querySelectorAll('[data-menu-category]')];
    this.search = this.querySelector('[data-header-search]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.desktop = matchMedia('(min-width: 75rem)');
    this.hover = matchMedia('(hover: hover) and (pointer: fine)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.row = this.querySelector('.store-header__row');
    this.bar = this.querySelector('.store-header__bar');
    this.path = [];
    this.navigationAnchor = document.createComment('header navigation');
    this.navigation?.before(this.navigationAnchor);
    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-controls', this.drawer.id);
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuToggle.setAttribute('aria-haspopup', 'dialog');
    }

    this.addEventListener('click', (event) => this.onClick(event), options);
    document.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.addEventListener('toggle', (event) => this.onToggle(event), { ...options, capture: true });
    this.addEventListener('pointerover', (event) => this.onPointerOver(event), options);
    this.addEventListener('pointerout', (event) => this.onPointerOut(event), options);
    this.addEventListener('focusout', (event) => {
      if (!event.relatedTarget) return;
      const branch = event.target.closest('[data-menu-branch]');
      if (!this.compact && branch && !branch.contains(event.relatedTarget)) this.setDisclosure(branch, false);
      if (this.search?.open && !this.search.contains(event.relatedTarget)) this.closeSearch();
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (this.search?.open && !this.search.contains(event.target)) {
        this.closeSearch(this.search.contains(document.activeElement));
      }
      if (!this.contains(event.target)) {
        this.closeBranches();
      }
    }, options);
    this.drawer?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDrawer();
    }, options);
    this.drawer?.addEventListener('close', () => {
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.resetPath();
    }, options);
    this.searchInput?.addEventListener('input', (event) => {
      if (!event.isComposing) this.queueSearch();
    }, options);
    this.searchInput?.addEventListener('compositionend', () => this.queueSearch(), options);
    this.searchInput?.addEventListener('beforeinput', (event) => {
      if (!this.replaceSearchOnInput || !event.inputType.startsWith('insert')) return;
      this.searchInput.select();
      this.replaceSearchOnInput = false;
    }, options);
    this.searchInput?.addEventListener('compositionstart', () => {
      if (this.replaceSearchOnInput) this.searchInput.select();
      this.replaceSearchOnInput = false;
    }, options);
    this.querySelector('[data-search-clear]')?.removeAttribute('hidden');
    this.desktop.addEventListener('change', () => this.resizeNavigation(), options);
    this.addEventListener('shopify:block:select', (event) => {
      const branch = event.target.closest('[data-menu-branch]');
      if (branch && !this.compact) this.openBranch(branch);
    }, options);

    this.resizeNavigation();
    this.dataset.ready = '';
    if (this.dataset.sticky === 'true') {
      const sentinel = this.querySelector('[data-header-sentinel]');
      this.toggleAttribute('data-scrolled', sentinel.getBoundingClientRect().bottom < 0);
      this.scrollObserver = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-scrolled', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        if (this.reducedMotion.matches) this.resizeNavigation();
        this.publishGeometry();
      });
      this.scrollObserver.observe(sentinel);
    }
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width === this.lastWidth) return;
      this.lastWidth = width;
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.resizeNavigation());
    });
    // The document-width wrapper stays stable while the inner bar morphs.
    this.resizeObserver.observe(this);
    this.ribbon = this.parentElement.querySelector('[data-announcement-ribbon]');
    this.geometryObserver = new ResizeObserver(() => this.publishGeometry());
    if (this.ribbon) this.geometryObserver.observe(this.ribbon);
    window.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
    this.geometryObserver.observe(this.bar);
    this.bar.addEventListener('transitionend', (event) => {
      if (event.target !== this.bar || !['width', 'inline-size'].includes(event.propertyName)) return;
      this.resizeNavigation();
      this.publishGeometry();
    }, options);
    window.addEventListener('resize', () => this.publishGeometry(), options);
    document.addEventListener('cart:opening', () => {
      this.closeSearch(false);
      this.closeDrawer(false);
      this.closeBranches();
    }, options);
    this.publishGeometry();
    document.fonts.ready.then(() => {
      if (this.isConnected && this.controller) this.resizeNavigation();
    });

    // The cart adapter updates these text nodes from Shopify's rendered section.
    const count = this.querySelector('[data-cart-count]');
    if (count) {
      this.cartCount = count.textContent;
      count.hidden = this.cartCount.trim() === '0';
      count.toggleAttribute('data-wide', this.cartCount.trim().length > 2);
      this.cartObserver = new MutationObserver(() => {
        if (count.textContent === this.cartCount) return;
        this.cartCount = count.textContent;
        count.hidden = this.cartCount.trim() === '0';
        count.toggleAttribute('data-wide', this.cartCount.trim().length > 2);
        if (!count.hidden && !this.reducedMotion.matches) {
          this.cartAnimation?.cancel();
          this.cartAnimation = count.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 240, easing: 'ease-out' });
        }
      });
      this.cartObserver.observe(count, { childList: true, characterData: true, subtree: true });
    }
  }

  onScroll() {
    // Publish the moving header's clearance only until it becomes a fixed capsule.
    if (this.ribbon && !this.hasAttribute('data-scrolled')) this.publishGeometry();
  }

  publishGeometry() {
    if (this.geometryFrame) return;
    this.geometryFrame = requestAnimationFrame(() => {
      this.geometryFrame = null;
      if (!this.isConnected) return;
      const naturalHeight = this.hasAttribute('data-scrolled') ? null : this.bar.offsetHeight;
      const headerHeight = this.dataset.topStyle === 'transparent' ? 0 : (naturalHeight ?? this.offsetHeight);
      const flowHeight = headerHeight + (this.ribbon?.offsetHeight || 0);
      const height = this.dataset.sticky === 'true' ? Math.ceil(this.bar.getBoundingClientRect().bottom) : 0;
      if (naturalHeight !== null && naturalHeight !== this.naturalHeight) {
        this.naturalHeight = naturalHeight;
        this.style.setProperty('--header-natural-height', `${naturalHeight}px`);
      }
      if (flowHeight !== this.flowHeight) {
        this.flowHeight = flowHeight;
        document.documentElement.style.setProperty('--header-flow-height', `${flowHeight}px`);
      }
      if (height === this.occupiedHeight) return;
      this.occupiedHeight = height;
      document.documentElement.style.setProperty('--header-occupied', `${height}px`);
      document.dispatchEvent(new CustomEvent('header:geometry', { detail: { height } }));
    });
  }

  resizeNavigation() {
    if (!this.navigation) return;
    const wasCompact = this.compact;
    if (wasCompact && !this.desktop.matches) return;
    const focused = document.activeElement;
    // Measure the intrinsic desktop row only on width/font changes, never on scroll.
    this.toggleAttribute('data-compact', false);
    this.menuShell.open = true;
    if (this.navigation.parentNode !== this.navigationAnchor.parentNode) this.navigationAnchor.after(this.navigation);
    let compact = !this.desktop.matches;
    if (!compact) {
      const list = this.navigation.querySelector('.header-navigation__list');
      compact = list.scrollWidth > this.navigation.clientWidth || this.row.scrollWidth > this.row.clientWidth;
    }
    this.compact = compact;
    this.toggleAttribute('data-compact', compact);
    this.menuToggle.hidden = !compact;
    this.menuShell.open = !compact;
    if (compact) this.querySelector('[data-menu-content]').append(this.navigation);
    const identity = this.querySelector('.store-header__identity');
    if (compact && this.row.firstElementChild !== this.menuShell) this.row.prepend(this.menuShell);
    if (!compact && identity.nextElementSibling !== this.menuShell) identity.after(this.menuShell);
    if (wasCompact !== compact) {
      this.closeDrawer(false);
      this.closeBranches();
      this.categories.forEach((category) => this.setDisclosure(category, !compact));
      if (this.navigation.contains(focused) || this.drawer.contains(focused) || focused === this.menuToggle) {
        if (compact) this.menuToggle.focus();
        else this.navigation.querySelector('summary, a')?.focus();
      }
    } else if (this.drawer.open && this.navigation.contains(focused)) {
      focused.focus({ preventScroll: true });
    }
  }

  setDisclosure(details, open) {
    details.open = open;
    details.querySelector(':scope > summary').setAttribute('aria-expanded', String(open));
  }

  openBranch(branch) {
    this.closeSearch();
    this.closeBranches(branch);
    this.setDisclosure(branch, true);
  }

  closeBranches(except) {
    clearTimeout(this.hoverTimer);
    this.branches.forEach((branch) => {
      if (branch !== except) this.setDisclosure(branch, false);
    });
  }

  onClick(event) {
    if (event.target.closest('[data-menu-toggle]')) {
      event.preventDefault();
      this.closeSearch();
      this.resetPath();
      document.dispatchEvent(new CustomEvent('header:opening'));
      this.drawer.showModal();
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.drawer.querySelector('[data-menu-close]').focus({ preventScroll: true });
    } else if (event.target.closest('[data-menu-close]')) {
      this.closeDrawer();
    } else if (event.target.closest('[data-menu-back]')) {
      this.goBack();
    } else if (event.target === this.drawer) {
      const bounds = this.drawer.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDrawer();
    } else if (event.target.closest('[data-search-clear]')) {
      this.clearSearch();
    } else if (event.target.closest('[data-search-all]')) {
      this.search.querySelector('form').requestSubmit();
    }
    const summary = event.target.closest('summary');
    if (!summary || !this.navigation?.contains(summary)) return;
    event.preventDefault();
    const details = summary.parentElement;
    if (this.compact) {
      this.setDisclosure(details, true);
      this.path.push(details);
      this.updatePath();
      details.querySelector('.header-navigation__all, .header-navigation__category-title')?.focus({ preventScroll: true });
    } else if (details.open) {
      this.setDisclosure(details, false);
    } else {
      this.openBranch(details);
    }
  }

  onToggle(event) {
    if (event.target === this.search) {
      this.search.querySelector('summary').setAttribute('aria-expanded', String(this.search.open));
      if (this.search.open) {
        document.dispatchEvent(new CustomEvent('header:opening'));
        this.closeBranches();
        this.searchInput.focus();
        this.searchInput.select();
        this.replaceSearchOnInput = Boolean(this.searchInput.value);
        if (this.searchInput.value.trim()) this.queueSearch();
      } else {
        this.cancelSearch();
      }
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      clearTimeout(this.hoverTimer);
      if (this.drawer?.open) {
        event.preventDefault();
        event.stopPropagation();
        if (this.path.length) this.goBack();
        else this.closeDrawer();
      } else if (this.search?.open) {
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch(true);
      } else {
        const branch = this.branches.find((item) => item.open);
        if (branch) {
          event.preventDefault();
          this.setDisclosure(branch, false);
          branch.querySelector('summary').focus();
        }
      }
    }
    if (event.key === 'ArrowDown' && event.target === this.searchInput) {
      const first = this.results?.querySelector('a, button');
      if (first) { event.preventDefault(); first.focus(); }
    }
  }

  onPointerOver(event) {
    if (this.compact || !this.hover.matches || event.pointerType === 'touch') return;
    const branch = event.target.closest('[data-menu-branch]');
    if (!branch || branch.contains(event.relatedTarget)) return;
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.openBranch(branch), 160);
  }

  onPointerOut(event) {
    if (this.compact || !this.hover.matches) return;
    const branch = event.target.closest('[data-menu-branch]');
    if (!branch || branch.contains(event.relatedTarget)) return;
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (!branch.contains(document.activeElement)) this.setDisclosure(branch, false);
    }, 240);
  }

  updatePath() {
    const current = this.path.at(-1);
    this.drawerTitle.textContent = current?.querySelector('[data-menu-label]').textContent || this.drawerTitle.dataset.rootTitle;
    this.back.hidden = !current;
    this.querySelector('[data-menu-content]').scrollTop = 0;
  }

  resetPath() {
    this.path = [];
    this.closeBranches();
    this.categories.forEach((category) => this.setDisclosure(category, !this.compact));
    if (this.drawerTitle) this.updatePath();
  }

  goBack() {
    const previous = this.path.pop();
    if (!previous) return;
    this.setDisclosure(previous, false);
    this.updatePath();
    previous.querySelector('summary').focus();
  }

  closeDrawer(restore = true) {
    if (!this.drawer?.open) return;
    this.drawer.close();
    this.menuToggle.setAttribute('aria-expanded', 'false');
    this.resetPath();
    if (restore && this.compact) this.menuToggle.focus();
  }

  closeSearch(restore = false) {
    if (!this.search?.open) return;
    this.search.open = false;
    this.search.querySelector('summary').setAttribute('aria-expanded', 'false');
    this.cancelSearch();
    if (restore) this.search.querySelector('summary').focus();
  }

  cancelSearch() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.requestTimer);
    this.searchRequest?.abort();
    this.searchRevision = (this.searchRevision || 0) + 1;
    this.results?.removeAttribute('aria-busy');
    if (this.status) this.status.textContent = '';
  }

  clearSearch() {
    this.replaceSearchOnInput = false;
    this.searchInput.value = '';
    this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.searchInput.focus();
  }

  queueSearch() {
    this.cancelSearch();
    if (!this.results) return;
    this.results.hidden = true;
    this.results.replaceChildren();
    const query = this.searchInput.value.trim();
    if (query.length < 2 || !this.search.open) return;
    this.searchTimer = setTimeout(() => this.loadResults(query, this.searchRevision), 180);
  }

  async loadResults(query, revision) {
    this.searchRequest = new AbortController();
    const request = this.searchRequest;
    const url = new URL(this.searchInput.dataset.predictiveUrl, location.origin);
    if (url.origin !== location.origin) return;
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'header-predictive-search');
    this.status.textContent = this.status.dataset.loading;
    this.results.setAttribute('aria-busy', 'true');
    this.requestTimer = setTimeout(() => request.abort(), 5000);
    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search response unavailable');
      const rendered = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = rendered.querySelector('[data-predictive-content]');
      if (!content) throw new Error('Search section unavailable');
      if (revision !== this.searchRevision || !this.search.open || !this.isConnected) return;
      // Only this theme's escaped, same-origin predictive section supplies result markup.
      this.results.replaceChildren(...content.childNodes);
      this.results.hidden = false;
      this.status.textContent = content.dataset.resultStatus;
    } catch {
      if (revision === this.searchRevision && this.search.open && this.isConnected) {
        this.status.textContent = this.status.dataset.error;
      }
    } finally {
      if (revision === this.searchRevision) {
        clearTimeout(this.requestTimer);
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  disconnectedCallback() {
    this.closeDrawer(false);
    this.cancelSearch();
    this.controller?.abort();
    this.controller = null;
    this.scrollObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.geometryObserver?.disconnect();
    this.cartObserver?.disconnect();
    this.cartAnimation?.cancel();
    clearTimeout(this.hoverTimer);
    cancelAnimationFrame(this.resizeFrame);
    cancelAnimationFrame(this.geometryFrame);
    this.geometryFrame = null;
    document.documentElement.style.removeProperty('--header-occupied');
    document.documentElement.style.removeProperty('--header-flow-height');
    if (this.navigationAnchor?.parentNode) this.navigationAnchor.after(this.navigation);
    this.navigationAnchor?.remove();
    this.categories?.forEach((category) => this.setDisclosure(category, false));
    delete this.dataset.ready;
    delete this.dataset.compact;
    delete this.dataset.scrolled;
    if (this.menuToggle) {
      this.menuToggle.hidden = false;
      this.menuToggle.removeAttribute('aria-expanded');
      this.menuToggle.removeAttribute('aria-haspopup');
      this.menuToggle.setAttribute('aria-controls', this.navigation.id);
      this.menuShell.open = false;
    }
  }
}

if (!customElements.get('store-header')) customElements.define('store-header', StoreHeader);
